/* ─────────────────────────────────────────────────────────────
   lala / theme.js
   Global utilities and non-critical UI: scroll reveals, header
   state, mobile nav, announcement bar, gallery thumbs, back-to-top.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // ───── tiny pubsub / namespace ─────
  var LA = (window.lala = window.lala || {});
  LA.$ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  LA.$$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  LA.on = function (el, ev, fn, opts) { ev.split(' ').forEach(function (e) { el.addEventListener(e, fn, opts || false); }); };
  LA.emit = function (name, detail) { document.dispatchEvent(new CustomEvent('lala:' + name, { detail: detail })); };
  LA.listen = function (name, fn) { document.addEventListener('lala:' + name, function (e) { fn(e.detail); }); };
  LA.money = function (cents) {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'lt', { style: 'currency', currency: window.Shopify && Shopify.currency && Shopify.currency.active || 'EUR' }).format((cents || 0) / 100); }
    catch (e) { return '€' + ((cents || 0) / 100).toFixed(2); }
  };
  LA.fetchJSON = function (url, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, opts.headers || {});
    return fetch(url, opts).then(function (r) { return r.json().then(function (d) { return r.ok ? d : Promise.reject(d); }); });
  };
  LA.debounce = function (fn, ms) {
    var t; return function () { var a = arguments, ctx = this; clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, a); }, ms); };
  };

  // ───── flash / toast ─────
  var flashEl;
  LA.flash = function (msg, opts) {
    opts = opts || {};
    if (!flashEl) {
      flashEl = document.createElement('div');
      flashEl.className = 'la-flash';
      flashEl.setAttribute('role', 'status');
      document.body.appendChild(flashEl);
    }
    flashEl.innerHTML = '';
    var txt = document.createElement('span');
    txt.textContent = msg;
    flashEl.appendChild(txt);
    if (opts.undo) {
      var u = document.createElement('button');
      u.className = 'la-flash__undo';
      u.type = 'button';
      u.textContent = opts.undoLabel || 'undo';
      u.addEventListener('click', function () { opts.undo(); LA.flash('', { hide: true }); });
      flashEl.appendChild(u);
    }
    clearTimeout(flashEl._t);
    if (opts.hide) { flashEl.classList.remove('is-on'); return; }
    flashEl.classList.add('is-on');
    flashEl._t = setTimeout(function () { flashEl.classList.remove('is-on'); }, opts.duration || 3600);
  };

  // ───── scroll reveal ─────
  // Reusable so dynamically-injected content (recommendations, load-more,
  // filtered grids) also reveals. A single shared observer watches every
  // not-yet-revealed .la-reveal it's handed.
  var revealIO = null;
  LA.reveal = function (root) {
    var items = LA.$$('.la-reveal:not(.is-in)', root || document);
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) { items.forEach(function (el) { el.classList.add('is-in'); }); return; }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    }
    items.forEach(function (el) { revealIO.observe(el); });
  };
  function initReveal() { LA.reveal(document); }

  // ───── sticky header state ─────
  function initHeader() {
    var header = LA.$('.la-header');
    if (!header) return;
    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 8); };
    onScroll();
    LA.on(window, 'scroll', onScroll, { passive: true });
  }

  // ───── mobile nav ─────
  function initMobileNav() {
    var btn = LA.$('[data-menu-toggle]');
    var nav = LA.$('#la-mobile-nav');
    var closeBtn = LA.$('[data-menu-close]', nav);
    if (!btn || !nav) return;
    var open = function () { nav.classList.add('is-open'); document.body.style.overflow = 'hidden'; nav.setAttribute('aria-hidden', 'false'); };
    var close = function () { nav.classList.remove('is-open'); document.body.style.overflow = ''; nav.setAttribute('aria-hidden', 'true'); };
    btn.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    LA.on(document, 'keydown', function (e) { if (e.key === 'Escape') close(); });
    // close on outside click
    LA.on(nav, 'click', function (e) { if (e.target === nav) close(); });
    LA.listen('route:change', close);
  }

  // ───── announcement dismissal ─────
  function initAnnounce() {
    var bar = LA.$('.la-announce');
    if (!bar) return;
    var close = LA.$('.la-announce__close', bar);
    if (!close) return;
    var KEY = 'la.announce.dismissed';
    try { if (sessionStorage.getItem(KEY) === bar.dataset.key) bar.style.display = 'none'; } catch (e) {}
    close.addEventListener('click', function () {
      bar.style.display = 'none';
      try { sessionStorage.setItem(KEY, bar.dataset.key || '1'); } catch (e) {}
    });
  }

  // ───── product gallery slider (scroll-snap track + dots + arrows) ─────
  // Native horizontal swipe on touch; arrows scroll one slide on desktop; dots
  // jump to a slide. Active state is derived from the track's scroll position,
  // so it stays correct whether the user swipes, clicks a dot, or taps an arrow.
  function initGallery() {
    LA.$$('[data-gallery]').forEach(function (gal) {
      var track  = LA.$('[data-gallery-track]', gal);
      var slides = LA.$$('[data-gallery-slide]', gal);
      var thumbs = LA.$$('[data-gallery-thumb]', gal);
      var strip  = LA.$('[data-gallery-thumbs]', gal);
      var prev   = LA.$('[data-gallery-prev]', gal);
      var next   = LA.$('[data-gallery-next]', gal);
      if (!track || slides.length < 2) return;

      var current = 0;
      function goTo(i) {
        var n = slides.length;
        var w = ((i % n) + n) % n;          // wrap: last→first, first→last
        // A wrap jump (past an end) is instant so it reads as a loop; steps are smooth.
        var isWrap = (i < 0 || i >= n);
        track.scrollTo({ left: w * track.clientWidth, behavior: isWrap ? 'auto' : 'smooth' });
      }
      // Centre the active thumb within the strip without scrolling the page.
      function revealThumb(t) {
        if (!strip || !t) return;
        var left = t.offsetLeft - (strip.clientWidth - t.clientWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
      function setActive(i) {
        current = i;
        thumbs.forEach(function (t, ti) {
          if (ti === i) { t.setAttribute('aria-current', 'true'); revealThumb(t); }
          else t.removeAttribute('aria-current');
        });
        // Arrows never disable — the gallery loops, so they stay clickable at both ends.
      }
      setActive(0);

      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });
      if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
      if (next) next.addEventListener('click', function () { goTo(current + 1); });

      // Derive the active slide from scroll position (covers swipe + smooth scroll).
      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var w = track.clientWidth || 1;
          var i = Math.round(track.scrollLeft / w);
          if (i !== current) setActive(i);
        });
      }, { passive: true });

      // Keyboard arrows when the gallery holds focus.
      gal.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });
    });
  }

  // ───── back to top ─────
  function initBackToTop() {
    var btn = LA.$('.la-back-to-top');
    if (!btn) return;
    btn.removeAttribute('hidden');
    var onScroll = function () { btn.classList.toggle('is-visible', window.scrollY > 800); };
    onScroll();
    LA.on(window, 'scroll', onScroll, { passive: true });
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ───── accordion (native <details> with smooth height animation) ─────
  function initAccordion() {
    LA.$$('.la-acc__item').forEach(function (item) {
      var summary = LA.$('summary', item);
      if (!summary) return;
      // Prevent double-open UI shift: trust native <details> toggle.
      item.addEventListener('toggle', function () {
        LA.emit('acc:toggle', { item: item, open: item.open });
      });
    });
  }

  // ───── scarcity (custom metafield-driven copy, per-visitor cookie decrement) ─────
  LA.getCookie = function (name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[2]) : null;
  };
  LA.setCookie = function (name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + (days || 30) * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  };
  function renderScarcity(el) {
    var pid = el.dataset.productId;
    var tmpl = el.dataset.template || '';
    var start = parseInt(el.dataset.count, 10) || 0;
    var ordered = parseInt(LA.getCookie('lala_ordered_' + pid) || '0', 10);
    var remaining = Math.max(1, start - ordered); // never show 0 — would imply sold out
    el.textContent = tmpl.replace('{count}', remaining);
  }
  function initScarcity() {
    LA.$$('[data-scarcity]').forEach(renderScarcity);
    // On successful add-to-cart, increment the ordered count for the added product
    LA.listen('cart:added', function (d) {
      if (!d || !d.cart || !d.cart.items) return;
      // The last item in cart is the most recent add (Shopify appends)
      var items = d.cart.items;
      if (!items.length) return;
      var item = items[items.length - 1];
      var pid = String(item.product_id);
      var key = 'lala_ordered_' + pid;
      var current = parseInt(LA.getCookie(key) || '0', 10);
      LA.setCookie(key, current + 1, 14);
      // Re-render any scarcity element on the current page for that product
      LA.$$('[data-scarcity][data-product-id="' + pid + '"]').forEach(renderScarcity);
    });
  }

  // ───── language & currency selects ─────
  function initLocalePicker() {
    LA.$$('form[data-locale-form] select').forEach(function (sel) {
      sel.addEventListener('change', function () { sel.form.submit(); });
    });
  }

  // ───── init all ─────
  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };
  ready(function () {
    initReveal();
    initHeader();
    initMobileNav();
    initAnnounce();
    initGallery();
    initBackToTop();
    initAccordion();
    initLocalePicker();
    initScarcity();
  });
})();
